import React, { useState, useEffect } from "react";
import { db, loginAdmin } from "../../Firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { base_url } from "../../Global";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Avatar from "@mui/material/Avatar";
import { CircleLoader } from "react-spinners";

const badgeStyle = (bg, color = "white") => ({
  fontSize: "11px",
  backgroundColor: bg,
  color,
  padding: "2px 7px",
  borderRadius: "999px",
  fontWeight: 700,
  letterSpacing: "0.05em",
});

const mutedPillStyle = {
  fontSize: "11px",
  backgroundColor: "#4b5563",
  color: "#d1d5db",
  padding: "2px 8px",
  borderRadius: "999px",
  fontWeight: 500,
};

const outlineBtnStyle = {
  background: "none",
  border: "1px solid #4b5563",
  borderRadius: "6px",
  color: "white",
  padding: "6px 14px",
  cursor: "pointer",
  fontSize: "13px",
  flexShrink: 0,
};

const solidBtnStyle = (bg) => ({
  padding: "7px 14px",
  borderRadius: "6px",
  border: "none",
  cursor: "pointer",
  backgroundColor: bg,
  color: "white",
  fontSize: "13px",
  fontWeight: 500,
  flexShrink: 0,
});

const STATUS_BADGES = {
  warned: badgeStyle("#d97706"),
  suspended: badgeStyle("#dc2626"),
  reactivated: badgeStyle("#059669"),
};

const ReportedUsers = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({}); // reportId → bool

  // ─── Fetch pending reports ────────────────────────────────────────────────
  const fetchReports = async () => {
    setLoading(true);
    try {
      await loginAdmin();
      const q = query(
        collection(db, "reports"),
        where("status", "in", ["pending", "warned", "suspended", "reactivated"])
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort newest first (timestamp may be null briefly after write)
      list.sort((a, b) => {
        const ta = a.timestamp?.seconds ?? 0;
        const tb = b.timestamp?.seconds ?? 0;
        return tb - ta;
      });
      setReports(list);
    } catch (e) {
      console.error("Error fetching reports:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const setActionBusy = (id, val) =>
    setActionLoading((prev) => ({ ...prev, [id]: val }));

  const sendEmail = async (to, subject, body, userId = "unknown") => {
    if (!to) return; // skip if no email on file
    try {
      const response = await fetch(base_url + "/auth/sendDirectEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject,
          to: [{ id: userId, email: to }],
          body,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        console.error("Email send failed:", err);
      }
    } catch (e) {
      console.error("Email send failed:", e);
    }
  };

  // ─── WARN action ─────────────────────────────────────────────────────────
  const handleWarn = async (report) => {
    setActionBusy(report.id, true);
    try {
      // 1. Update report status
      await updateDoc(doc(db, "reports", report.id), { status: "warned" });

      // 2. Fetch user email if not on report doc
      let email = report.reportedUserEmail || "";
      if (!email && report.reportedUser) {
        const userSnap = await getDoc(doc(db, "users", report.reportedUser));
        email = userSnap.data()?.email || "";
      }

      // 3. Send warning email
      const subject = "⚠️ Community Guidelines Warning — Vortex";
      const body = `Hi ${report.reportedUserName || "there"},

We're reaching out because your Vortex profile has been reported by another member of our community.

After reviewing the report, our moderation team has determined that some of your activity may not align with our Community Guidelines.

📌 Reported reason: "${report.reason}"

This is an official warning. Continued violations of our guidelines may result in temporary or permanent suspension of your account.

Please review our Community Guidelines at https://vortexapp.com/guidelines and ensure your behaviour meets our standards.

If you believe this warning was issued in error, please contact our support team at Info@legacyx.uk.

Warm regards,
The Vortex Team
https://www.legacyx.uk/`;

      await sendEmail(email, subject, body, report.reportedUser || "unknown");

      toast.success(`⚠️ Warning sent to ${report.reportedUserName || "user"}`);
      // Update local state instead of removing
      setReports((prev) =>
        prev.map((r) => (r.id === report.id ? { ...r, status: "warned" } : r))
      );
    } catch (e) {
      console.error("Warn error:", e);
      toast.error("Failed to send warning");
    }
    setActionBusy(report.id, false);
  };

  // ─── SUSPEND action ───────────────────────────────────────────────────────
  const handleSuspend = async (report) => {
    setActionBusy(report.id, true);
    try {
      // 1. Mark user as suspended
      if (report.reportedUserId) {
        await updateDoc(doc(db, "users", report.reportedUserId), {
          isSuspended: true,
        });
      }

      // 2. Update report status
      await updateDoc(doc(db, "reports", report.id), { status: "suspended" });

      // 3. Fetch user email if needed
      let email = report.reportedUserEmail || "";
      if (!email && report.reportedUser) {
        const userSnap = await getDoc(doc(db, "users", report.reportedUser));
        email = userSnap.data()?.email || "";
      }

      // 4. Send suspension email
      const subject = "🚫 Account Suspended — Vortex";
      const body = `Hi ${report.reportedUserName || "there"},

Our moderation team carefully reviewed the report and concluded that your account activity violates our Community Guidelines. As a result, your access to Vortex has been temporarily suspended.

What this means for you:
• You will not be able to log in to your Vortex account during the suspension period.
• Your profile will not be visible to other users.

If you believe this decision was made in error or would like to appeal, please contact our support team at Info@legacyx.uk with the subject line "Suspension Appeal". We will review your case within 3–5 business days.

We take the safety of our community very seriously and appreciate your understanding.

Regards,
The Vortex Team
https://www.legacyx.uk/`;

      await sendEmail(email, subject, body, report.reportedUser || "unknown");

      toast.success(
        `🚫 ${report.reportedUserName || "User"} suspended successfully`
      );
      setReports((prev) =>
        prev.map((r) => (r.id === report.id ? { ...r, status: "suspended" } : r))
      );
    } catch (e) {
      console.error("Suspend error:", e);
      toast.error("Failed to suspend user");
    }
    setActionBusy(report.id, false);
  };

  // ─── REACTIVATE action ────────────────────────────────────────────────────
  const handleReactivate = async (report) => {
    setActionBusy(report.id, true);
    try {
      // 1. Mark user as active
      if (report.reportedUserId) {
        await updateDoc(doc(db, "users", report.reportedUserId), {
          isSuspended: false,
        });
      }

      // 2. Update report status
      await updateDoc(doc(db, "reports", report.id), { status: "reactivated" });

      // 3. Fetch user email if needed
      let email = report.reportedUserEmail || "";
      if (!email && report.reportedUser) {
        const userSnap = await getDoc(doc(db, "users", report.reportedUser));
        email = userSnap.data()?.email || "";
      }

      // 4. Send reactivation email
      const subject = "✅ Account Reactivated — Vortex";
      const body = `Hi ${report.reportedUserName || "there"},

Good news! Our moderation team has reviewed your account and lifted your suspension. You can now log back into Vortex and continue using the app.

We kindly ask that you review our Community Guidelines (https://vortexapp.com/guidelines) to ensure your future activity aligns with our standards.

Welcome back!

Warm regards,
The Vortex Team
https://www.legacyx.uk/`;

      await sendEmail(email, subject, body, report.reportedUser || "unknown");

      toast.success(`✅ ${report.reportedUserName || "User"} reactivated successfully`);
      setReports((prev) =>
        prev.map((r) => (r.id === report.id ? { ...r, status: "reactivated" } : r))
      );
    } catch (e) {
      console.error("Reactivate error:", e);
      toast.error("Failed to reactivate user");
    }
    setActionBusy(report.id, false);
  };

  // ─── DISMISS action ───────────────────────────────────────────────────────
  const handleDismiss = async (report) => {
    setActionBusy(report.id, true);
    try {
      await updateDoc(doc(db, "reports", report.id), { status: "dismissed" });
      toast.info(`Report dismissed`);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (e) {
      toast.error("Failed to dismiss report");
    }
    setActionBusy(report.id, false);
  };

  // ─── DELETE action ────────────────────────────────────────────────────────
  const handleDeleteUser = async (report) => {
    if (!window.confirm("Are you sure you want to permanently delete this user? This action cannot be undone.")) return;

    setActionBusy(report.id, true);
    try {
      const uid = report.reportedUserId || report.reportedUser;

      // Fetch user email if needed before deleting the user document
      let email = report.reportedUserEmail || "";
      if (!email && uid) {
        const userSnap = await getDoc(doc(db, "users", uid));
        email = userSnap.data()?.email || "";
      }

      if (uid) {
        await deleteDoc(doc(db, "users", uid));
      }

      await deleteDoc(doc(db, "reports", report.id));

      // Send deletion email
      const subject = "❌ Account Deleted — Vortex";
      const body = `Hi ${report.reportedUserName || "there"},

After reviewing the reports against your account, our moderation team has determined that your activity severely violated our Community Guidelines.

As a result, your Vortex account has been permanently deleted.

This decision is final. You will no longer be able to access your account, and your profile is no longer visible on Vortex.

Regards,
The Vortex Team
https://www.legacyx.uk/`;

      await sendEmail(email, subject, body, uid || "unknown");

      toast.success(`🗑️ ${report.reportedUserName || "User"} deleted permanently`);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (e) {
      console.error("Delete user error:", e);
      toast.error("Failed to delete user");
    }
    setActionBusy(report.id, false);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const formatDate = (ts) => {
    if (!ts?.seconds) return "—";
    return new Date(ts.seconds * 1000).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div style={{ color: "white" }}>
      <ToastContainer />

      {/* HEADER — mirrors the Users section header above it */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: "clamp(1.25rem, 4vw, 2rem)", textTransform: "capitalize", fontWeight: 700 }}>
            Reported Users
          </h1>
          <div style={{ border: "0.6px solid white", borderRadius: "6px", padding: "8px 12px", fontSize: "14px" }}>
            Reports: {reports.length}
          </div>
        </div>

        <button onClick={fetchReports} style={outlineBtnStyle}>
          Refresh
        </button>
      </div>

      {/* BODY */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
          <CircleLoader color="#4A90E2" size={50} />
        </div>
      ) : reports.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
          ✅ No reported users
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxHeight: "70vh", overflowY: "auto" }}>
          {reports.map((report) => {
            const busy = actionLoading[report.id];

            return (
              <div
                key={report.id}
                style={{
                  backgroundColor: "#1f2937",
                  borderRadius: "8px",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {/* Left: avatar, name, badges, reason/date */}
                <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1, minWidth: 0 }}>
                  <Avatar
                    src={report.reportedUserImage || ""}
                    alt={report.reportedUserName}
                    sx={{ width: 48, height: 48, flexShrink: 0 }}
                    imgProps={{ onError: (e) => { e.target.src = ""; } }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", fontWeight: 600, fontSize: "15px" }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {report.reportedUserName || "Unknown"}
                      </span>
                      {report.status && report.status !== "pending" && (
                        <span style={STATUS_BADGES[report.status] || mutedPillStyle}>
                          {report.status.toUpperCase()}
                        </span>
                      )}
                      <span style={mutedPillStyle}>
                        🚩 {report.reason || "No reason given"}
                      </span>
                      <span style={mutedPillStyle}>
                        🕒 {formatDate(report.timestamp)}
                      </span>
                    </div>
                    <div style={{ fontSize: "13px", color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "320px" }}>
                      {report.reportedUserEmail || (report.reportedUser ? `${report.reportedUser.slice(0, 10)}…` : "No email")}
                    </div>
                  </div>
                </div>

                {/* Right: action buttons */}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    disabled={busy}
                    onClick={() => handleWarn(report)}
                    style={{ ...solidBtnStyle(report.status === "warned" ? "#92400e" : "#d97706"), opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                  >
                    {report.status === "warned" ? "Warned" : "Warn"}
                  </button>

                  {report.status === "suspended" ? (
                    <button
                      disabled={busy}
                      onClick={() => handleReactivate(report)}
                      style={{ ...solidBtnStyle("#059669"), opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      Reactivate
                    </button>
                  ) : (
                    <button
                      disabled={busy}
                      onClick={() => handleSuspend(report)}
                      style={{ ...solidBtnStyle("#dc2626"), opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      Suspend
                    </button>
                  )}

                  <button
                    disabled={busy}
                    onClick={() => handleDismiss(report)}
                    style={{ ...outlineBtnStyle, opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                  >
                    Dismiss
                  </button>

                  <button
                    disabled={busy}
                    onClick={() => handleDeleteUser(report)}
                    title="Delete User"
                    style={{ ...solidBtnStyle("#ef4444"), opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReportedUsers;